import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FaRegFileImage,
    FaRegFileVideo,
    FaUpload,
    FaArrowLeft,
    FaPlay,
    FaPause,
    FaExpand,
    FaCompress,
    FaVolumeUp,
    FaVolumeMute,
    FaClock
} from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { toast } from 'react-toastify';

// Import components
import Loader from '../../../components/Loader';
import FileUpload from '../../../components/ul/FileUpload';
import AdsNavbar from '../AdsNavbar';
import Calling from '../../../api/Calling';

// Import utilities
import {
    imageSizeExceededMessage,
    MAX_FILE_SIZE_IMAGE,
    videoSizeExceededMessage,
    videoType,
    invalidVideoMessage,
    MAX_FILE_SIZE_VIDEO
} from '../../../components/imageType';
import { AiOutlineFundView } from 'react-icons/ai';

const EditInHouseAds = () => {
    const [loader, setLoader] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const [formInfo, setFormInfo] = useState({
        mainTitle: '',
        subTitle: '',
        medType: 'image',
        description: '',
        callToAction: '',
        btnName: '',
        medView: '',
        view: 0,
        clickCount: 0,
        icon: '',
        web_stay_time: 1,
        adType: 'ntv',
        direct_visit: false,
    });

    const [file, setFile] = useState(true);
    const [imageFile, setImageFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [iconFile, setIconFile] = useState(null);
    const [fileProcessing, setFileProcessing] = useState({
        icon: false,
        image: false,
        video: false
    });
    const [fileStatus, setFileStatus] = useState({
        icon: null,
        image: null,
        video: null
    });

    const imageType = [
        'image/png',
        'image/jpg',
        'image/jpeg',
        'image/gif'
    ];
    const invalidImageMessage = "Please select a valid image file. Allowed types: 'image/png', 'image/jpg', 'image/jpeg', 'image/gif'";

    // Preview video controls
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [previewType, setPreviewType] = useState('all');
    const videoRef = useRef(null);
    const previewContainerRef = useRef(null);

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getInHouseAdsData(id);
            if (resp?.status) {
                // If web_stay_time doesn't exist in the response, set a default value of 1
                const adData = {
                    ...resp?.data,
                    web_stay_time: resp?.data?.web_stay_time || 1
                };
                setFormInfo(adData);
            } else {
                toast.error('Failed to load ad data');
                navigate('/manage/ads/inHouseAds/add');
            }
        } catch (error) {
            console.error(error);
            toast.error('An error occurred while loading data');
            navigate('/manage/ads/inHouseAds/add');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    useEffect(() => {
        // Add event listener for fullscreen change
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement && previewContainerRef.current) {
            if (previewContainerRef.current.requestFullscreen) {
                previewContainerRef.current.requestFullscreen();
            } else if (previewContainerRef.current.webkitRequestFullscreen) {
                previewContainerRef.current.webkitRequestFullscreen();
            } else if (previewContainerRef.current.msRequestFullscreen) {
                previewContainerRef.current.msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate web_stay_time
        if (formInfo.web_stay_time < 1) {
            return toast.error('Web stay time must be at least 1 second');
        }

        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('id', id);
            formData.append('mainTitle', formInfo.mainTitle);
            formData.append('subTitle', formInfo.subTitle);
            formData.append('medType', formInfo.medType);
            formData.append('description', formInfo.description);
            formData.append('callToAction', formInfo.callToAction);
            formData.append('btnName', formInfo.btnName);
            formData.append('web_stay_time', formInfo.web_stay_time);
            formData.append('adType', formInfo.adType);
            formData.append('direct_visit', formInfo.direct_visit);

            // if (iconFile) {
            //     formData.append('icon', iconFile);
            // }

            if (formInfo?.medType === 'image') {
                if (imageFile) {
                    formData.append('image', imageFile);
                    formData.append('icon', imageFile);
                }
            } else {
                if (videoFile) {
                    formData.append('video', videoFile);
                }
            }

            const resp = await Calling.updateInHouseAds(formData);
            if (resp?.status) {
                toast.success(resp?.message || 'Updated successfully!');
                navigate('/manage/ads/inHouseAds/add');
            } else {
                toast.error(resp?.message || 'Update failed!');
            }
        } catch (error) {
            console.log(error);
            toast.error('An error occurred during update');
        } finally {
            setLoader(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        // For number inputs, convert the value to a number
        const processedValue = type === 'number' ? Number(value) : value;
        setFormInfo({ ...formInfo, [name]: processedValue });
    };

    const handleChangeFile = (e) => {
        const { name, files, id } = e.target;
        const file = files[0];

        if (file) {
            const isImage = name === 'image';
            const isIcon = name === 'icon';
            const isVideo = name === 'video';

            // Determine the file type and max size
            let isValidType = false;
            let maxSize = 0;
            let errorMessage = '';

            if (isImage || isIcon) {
                isValidType = imageType.includes(file.type);
                maxSize = MAX_FILE_SIZE_IMAGE;
                errorMessage = imageSizeExceededMessage;
            } else if (isVideo) {
                isValidType = videoType.includes(file.type);
                maxSize = MAX_FILE_SIZE_VIDEO;
                errorMessage = videoSizeExceededMessage;
            }

            // Validate file type
            if (!isValidType) {
                const fileTypeError = isImage || isIcon ? invalidImageMessage : invalidVideoMessage;
                if (isImage) setImageFile(null);
                if (isIcon) setIconFile(null);
                if (isVideo) setVideoFile(null);
                setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
                setFileStatus((prevState) => ({ ...prevState, [id]: 'error' }));
                toast.error(fileTypeError);
                setFile(false);
                e.target.value = '';
                return;
            }

            // Validate file size
            if (file.size > maxSize) {
                toast.error(errorMessage);
                if (isImage) setImageFile(null);
                if (isIcon) setIconFile(null);
                if (isVideo) setVideoFile(null);
                setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
                setFileStatus((prevState) => ({ ...prevState, [id]: 'error' }));
                setFile(false);
                e.target.value = '';
                return;
            }

            // Start processing the file
            setFileProcessing((prevState) => ({ ...prevState, [id]: true }));
            setFileStatus((prevState) => ({ ...prevState, [id]: null }));

            // Simulate file processing
            setTimeout(() => {
                setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
                setFileStatus((prevState) => ({ ...prevState, [id]: 'success' }));
            }, 1000);

            // Update the state with the appropriate file type
            if (isImage) {
                setImageFile(file);
                setFile(true);
            } else if (isIcon) {
                setIconFile(file);
                setFile(true);
            } else if (isVideo) {
                setVideoFile(file);
                setFile(true);
            }
        }
    };

    const handleCancel = () => {
        navigate('/manage/ads/inHouseAds/add');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num;
    };



    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='md:ml-[4.4rem] w-full text-main-font mt-16 px-3'>
                    <h1 className="text-2xl font-bold mt-4 text-main-font w-auto">Ad Management</h1>

                    <div className='bg-white rounded-xl shadow-md overflow-hidden'>
                        <AdsNavbar selectState={'inhouseads'} />
                        <hr className='mt-2 bg-gray-100 border-[1px]' />

                        <div className='p-6'>
                            <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4'>
                                <div className="flex items-center">
                                    <button
                                        onClick={handleCancel}
                                        className="mr-3 text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors duration-200"
                                    >
                                        <FaArrowLeft size={16} />
                                    </button>
                                    <h1 className='text-xl md:text-2xl font-bold text-gray-800'>Edit Advertisement</h1>
                                </div>

                                <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                                    <div className="flex flex-col items-end">
                                        <div className="text-sm text-gray-500">Created</div>
                                        <div className="text-sm font-medium">{formInfo.createdAt ? formatDate(formInfo.createdAt) : 'N/A'}</div>
                                    </div>
                                    <div className="h-8 border-l border-gray-300"></div>
                                    <div className="flex flex-col items-end">
                                        <div className="text-sm text-gray-500">Views</div>
                                        <div className="text-sm font-medium text-blue-600">{formatNumber(formInfo.view || 0)}</div>
                                    </div>
                                    <div className="h-8 border-l border-gray-300"></div>
                                    <div className="flex flex-col items-end">
                                        <div className="text-sm text-gray-500">Clicks</div>
                                        <div className="text-sm font-medium text-green-600">{formatNumber(formInfo.clickCount || 0)}</div>
                                    </div>
                                    <div className="h-8 border-l border-gray-300"></div>
                                    <div className="flex flex-col items-end">
                                        <div className="text-sm text-gray-500">Stay Time</div>
                                        <div className="text-sm font-medium text-orange-600">{formInfo.web_stay_time || 1}s</div>
                                    </div>
                                    <div className="h-8 border-l border-gray-300"></div>
                                </div>
                            </div>

                            <hr className='border-gray-200 mb-6' />

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Form Section - 2/3 width on large screens */}
                                <div className="lg:col-span-2">
                                    <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-6">
                                        {/* Title and Subtitle */}
                                        {/* <div className='space-y-4'>
                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='mainTitle'>
                                                    Title
                                                </label>
                                                <input
                                                    type='text'
                                                    id='mainTitle'
                                                    name='mainTitle'
                                                    value={formInfo.mainTitle}
                                                    onChange={handleChange}
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                    placeholder='Enter Main Title'
                                                    required
                                                />
                                            </div>

                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2' htmlFor='subTitle'>
                                                    Subtitle
                                                </label>
                                                <input
                                                    type='text'
                                                    id='subTitle'
                                                    name='subTitle'
                                                    value={formInfo.subTitle}
                                                    onChange={handleChange}
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                    placeholder='Enter Subtitle'
                                                    required
                                                />
                                            </div>
                                        </div> */}


                                        {/* Description */}
                                        {/* <div className='flex flex-col'>
                                            <label className='text-gray-700 font-medium mb-2' htmlFor='description'>
                                                Details
                                            </label>
                                            <textarea
                                                id='description'
                                                name='description'
                                                value={formInfo.description}
                                                onChange={handleChange}
                                                className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                placeholder='Enter Description'
                                                rows="4"
                                                required
                                            />
                                        </div> */}

                                        {/* CTA, Button Name, and Web Stay Time */}
                                        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                                            {/* Type */}
                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='medType'>
                                                    {formInfo.medType === 'image' ? (
                                                        <FaRegFileImage className="mr-2 text-blue-500" />
                                                    ) : (
                                                        <FaRegFileVideo className="mr-2 text-red-500" />
                                                    )}
                                                    Media Type
                                                </label>
                                                <select
                                                    id='medType'
                                                    name='medType'
                                                    value={formInfo.medType}
                                                    onChange={handleChange}
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                >
                                                    <option value="image">Image</option>
                                                    {/* <option value="video">Video</option> */}
                                                </select>
                                            </div>
                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='adType'>
                                                    <FaRegFileImage className="mr-2 text-blue-500" />
                                                    Ad Type
                                                </label>
                                                <select
                                                    id='adType'
                                                    name='adType'
                                                    value={formInfo.adType}
                                                    onChange={handleChange}
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                >
                                                    <option value="fs">Full Screen</option>
                                                    <option value="ntv">Natvie</option>
                                                </select>
                                            </div>
                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2' htmlFor='callToAction'>
                                                    Call to Action URL
                                                </label>
                                                <input
                                                    type='text'
                                                    id='callToAction'
                                                    name='callToAction'
                                                    value={formInfo.callToAction}
                                                    onChange={handleChange}
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                    placeholder='https://example.com/action'
                                                    required
                                                />
                                            </div>
                                            {/* <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2' htmlFor='btnName'>
                                                    Button Text
                                                </label>
                                                <input
                                                    type='text'
                                                    id='btnName'
                                                    name='btnName'
                                                    value={formInfo.btnName}
                                                    onChange={handleChange}
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                    placeholder='Learn More'
                                                    required
                                                />
                                            </div> */}
                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2 flex items-center' htmlFor='web_stay_time'>
                                                    <FaClock className="mr-2 text-orange-500" />
                                                    Web Stay Time (seconds)
                                                </label>
                                                <input
                                                    type='number'
                                                    id='web_stay_time'
                                                    name='web_stay_time'
                                                    value={formInfo.web_stay_time}
                                                    onChange={handleChange}
                                                    min="1"
                                                    step="1"
                                                    className='w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-200'
                                                    placeholder='Minimum time in seconds'
                                                    required
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Minimum time (in seconds) users must stay on the page</p>
                                            </div>
                                            {
                                                formInfo?.adType === 'fs' &&
                                                <div className='flex flex-col justify-center'>
                                                    <label className='text-gray-700 font-medium mb-2 flex items-center cursor-pointer' htmlFor='direct_visit'>
                                                        <input
                                                            type='checkbox'
                                                            id='direct_visit'
                                                            name='direct_visit'
                                                            checked={formInfo.direct_visit}
                                                            onChange={(e) => setFormInfo({ ...formInfo, 'direct_visit': e.target.checked })}
                                                            className='w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-2 focus:ring-blue-400 mr-2'
                                                        />
                                                        <AiOutlineFundView className="mr-2 text-orange-500" />
                                                        Direct Visit
                                                    </label>
                                                    <p className="text-xs text-gray-500 mt-1">Enable if users should be auto-redirected to the website</p>
                                                </div>
                                            }

                                        </div>

                                        {/* File Uploads */}
                                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                            {/* <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2' htmlFor='icon'>
                                                    Icon (Brand Logo)
                                                </label>
                                                <div className='relative bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-blue-400 transition-all duration-200'>
                                                    <FileUpload
                                                        id="icon"
                                                        name='icon'
                                                        accept='image/png,image/jpg,image/jpeg,image/gif'
                                                        onChange={handleChangeFile}
                                                        required={false}
                                                        className="relative z-10"
                                                    />
                                                    {fileProcessing['icon'] ? (
                                                        <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg'>
                                                            <div className='w-8 h-8 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin'></div>
                                                        </div>
                                                    ) : fileStatus['icon'] === 'error' ? (
                                                        <div className='absolute top-4 right-4 text-red-500'>✘</div>
                                                    ) : fileStatus['icon'] === 'success' ? (
                                                        <div className='absolute top-4 right-4 text-green-500'>✓</div>
                                                    ) : null}
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">Current icon will be used if no new file is selected</p>
                                            </div> */}

                                            <div className='flex flex-col'>
                                                <label className='text-gray-700 font-medium mb-2' htmlFor={formInfo.medType}>
                                                    {formInfo.medType === 'image' ? 'Advertisement Image' : 'Advertisement Video'}
                                                </label>
                                                <div className='relative bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-blue-400 transition-all duration-200'>
                                                    <FileUpload
                                                        id={formInfo.medType}
                                                        name={formInfo.medType}
                                                        accept={formInfo.medType === 'image' ? 'image/png,image/jpg,image/jpeg,image/gif' : 'video/mp4'}
                                                        onChange={handleChangeFile}
                                                        required={false}
                                                        className="relative z-10"
                                                    />
                                                    {fileProcessing[formInfo.medType] ? (
                                                        <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg'>
                                                            <div className='w-8 h-8 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin'></div>
                                                        </div>
                                                    ) : fileStatus[formInfo.medType] === 'error' ? (
                                                        <div className='absolute top-4 right-4 text-red-500'>✘</div>
                                                    ) : fileStatus[formInfo.medType] === 'success' ? (
                                                        <div className='absolute top-4 right-4 text-green-500'>✓</div>
                                                    ) : null}
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">Current {formInfo.medType} will be used if no new file is selected</p>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className='flex flex-col sm:flex-row gap-4 pt-4'>
                                            <button
                                                type="submit"
                                                className="bg-main-blue hover:bg-main-blue/80 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 font-medium"
                                                disabled={loader}
                                            >
                                                {loader ? (
                                                    <div className='w-5 h-5 border-2 border-t-white border-white/20 rounded-full animate-spin mr-2'></div>
                                                ) : (
                                                    <FaUpload className="mr-2" />
                                                )}
                                                {loader ? 'Updating...' : 'Update Ad'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCancel}
                                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 font-medium"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                {/* Preview Section - 1/3 width on large screens */}
                                <div className="lg:col-span-1">
                                    <div className="bg-gray-50 rounded-lg p-5 shadow-inner">
                                        <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                                            <svg className="mr-2" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 5C5.63636 5 1 12 1 12C1 12 5.63636 19 12 19C18.3636 19 23 12 23 12C23 12 18.3636 5 12 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Ad Preview
                                        </h3>

                                        {/* Preview Controls */}
                                        <div className="flex mb-4 border-b border-gray-200 pb-2">
                                            <button
                                                className={`px-3 py-1 rounded-md text-sm font-medium mr-2 ${previewType === 'all' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => setPreviewType('all')}
                                            >
                                                All
                                            </button>
                                            <button
                                                className={`px-3 py-1 rounded-md text-sm font-medium mr-2 ${previewType === 'icon' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => setPreviewType('icon')}
                                            >
                                                Icon
                                            </button>
                                            <button
                                                className={`px-3 py-1 rounded-md text-sm font-medium ${previewType === 'media' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => setPreviewType('media')}
                                            >
                                                {formInfo.medType === 'image' ? 'Image' : 'Video'}
                                            </button>
                                        </div>

                                        {/* Preview Content */}
                                        <div className="space-y-6">
                                            {/* Icon Preview */}
                                            {(previewType === 'all' || previewType === 'icon') && formInfo?.icon && (
                                                <div ref={previewContainerRef} className='bg-white border border-gray-200 p-4 rounded-lg shadow-sm'>
                                                    <p className="text-sm font-medium mb-3 text-gray-700">Brand Icon</p>
                                                    <div className='flex justify-center'>
                                                        <img
                                                            src={formInfo?.icon}
                                                            alt="Icon Preview"
                                                            className="w-28 h-28 object-cover rounded-full border-4 border-gray-100 shadow-sm"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Media Preview */}
                                            {(previewType === 'all' || previewType === 'media') && (
                                                <>
                                                    {/* Image Preview */}
                                                    {formInfo?.medType === 'image' && formInfo?.medView && (
                                                        <div className='bg-white border border-gray-200 p-4 rounded-lg shadow-sm'>
                                                            <p className="text-sm font-medium mb-3 text-gray-700">Advertisement Image</p>
                                                            <div className='flex justify-center'>
                                                                <img
                                                                    src={formInfo.medView}
                                                                    alt="Ad Preview"
                                                                    className="max-w-full max-h-[300px] object-contain rounded-md"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Video Preview */}
                                                    {formInfo?.medType === 'video' && formInfo?.medView && (
                                                        <div className='bg-white border border-gray-200 p-4 rounded-lg shadow-sm'>
                                                            <p className="text-sm font-medium mb-3 text-gray-700">Advertisement Video</p>
                                                            <div className='flex justify-center relative bg-black rounded-md'>
                                                                <video
                                                                    ref={videoRef}
                                                                    src={formInfo?.medView}
                                                                    className="max-w-full max-h-[300px] rounded-md"
                                                                    onClick={togglePlay}
                                                                    onPlay={() => setIsPlaying(true)}
                                                                    onPause={() => setIsPlaying(false)}
                                                                    onEnded={() => setIsPlaying(false)}
                                                                />

                                                                {/* Video Controls */}
                                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 flex items-center">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            togglePlay();
                                                                        }}
                                                                        className="text-white p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                                                                    >
                                                                        {isPlaying ? <FaPause size={16} /> : <FaPlay size={16} />}
                                                                    </button>

                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleMute();
                                                                        }}
                                                                        className="text-white p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors duration-200 ml-2"
                                                                    >
                                                                        {isMuted ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
                                                                    </button>

                                                                    <div className="flex-grow"></div>

                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleFullscreen();
                                                                        }}
                                                                        className="text-white p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                                                                    >
                                                                        {isFullscreen ? <FaCompress size={16} /> : <FaExpand size={16} />}
                                                                    </button>
                                                                </div>

                                                                {/* Play Overlay */}
                                                                {!isPlaying && (
                                                                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                                                        <div className="bg-white bg-opacity-80 rounded-full p-3">
                                                                            <FaPlay className="text-gray-800" size={20} />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Full Ad Preview */}
                                            {previewType === 'all' && (
                                                <div className='bg-white border border-gray-200 p-4 rounded-lg shadow-sm mt-4'>
                                                    <p className="text-sm font-medium mb-3 text-gray-700">Complete Ad Preview</p>

                                                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                                        <div className="flex items-center mb-3">
                                                            {formInfo?.icon && (
                                                                <img
                                                                    src={formInfo.icon}
                                                                    alt="Brand Icon"
                                                                    className="w-10 h-10 rounded-full mr-3 object-cover"
                                                                />
                                                            )}
                                                            <div>
                                                                <h4 className="font-medium text-gray-900">{formInfo.mainTitle || 'Ad Title'}</h4>
                                                                <p className="text-sm text-gray-600">{formInfo.subTitle || 'Subtitle'}</p>
                                                            </div>
                                                        </div>

                                                        <p className="text-sm text-gray-700 mb-3">{formInfo.description || 'Advertisement description will appear here.'}</p>

                                                        <div className="flex items-center mb-2">
                                                            <FaClock className="text-orange-500 mr-2" size={14} />
                                                            <span className="text-sm text-orange-600">
                                                                {formInfo.web_stay_time || 1}s minimum stay time
                                                            </span>
                                                        </div>

                                                        <a
                                                            href={formInfo.callToAction}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-block bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                                                        >
                                                            {formInfo.btnName || 'Learn More'}
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditInHouseAds;